import React, { useRef, forwardRef } from 'react';

/**
 * Accessibility wrapper for ARIA tabs pattern with manual activation.
 * Implements proper keyboard navigation for horizontal/vertical tabs.
 *
 * Manual activation means:
 * - Arrow keys move focus between tabs WITHOUT activating them
 * - Space/Enter keys activate the focused tab
 * - This pattern is used when tab content has loading latency
 */
const TabAccessibilityWrap = forwardRef((props, ref) => {
    const {className, children, orientation = 'horizontal', ...other} = props;
    const wrapperRef = ref ? ref : useRef();

    const handleKeyDown = (event) => {
        const { key } = event;
        const activeElement = document.activeElement;

        // Only handle keyboard events if focus is on a tab
        if (!activeElement.hasAttribute('role') || activeElement.getAttribute('role') !== 'tab') {
            return;
        }

        const isHorizontal = orientation === 'horizontal';

        switch (key) {
            case 'ArrowLeft':
                if (isHorizontal) {
                    event.preventDefault();
                    navigateTabs(activeElement, 'prev');
                }
                break;
            case 'ArrowRight':
                if (isHorizontal) {
                    event.preventDefault();
                    navigateTabs(activeElement, 'next');
                }
                break;
            case 'ArrowUp':
                if (!isHorizontal) {
                    event.preventDefault();
                    navigateTabs(activeElement, 'prev');
                }
                break;
            case 'ArrowDown':
                if (!isHorizontal) {
                    event.preventDefault();
                    navigateTabs(activeElement, 'next');
                }
                break;
            case 'Home':
                event.preventDefault();
                const firstTab = getFirstTab();
                if (firstTab) {
                    firstTab.focus();
                }
                break;
            case 'End':
                event.preventDefault();
                const lastTab = getLastTab();
                if (lastTab) {
                    lastTab.focus();
                }
                break;
            case ' ':
            case 'Enter':
                event.preventDefault();
                // Activate the focused tab (manual activation pattern)
                activeElement.click();
                break;
            default:
                break;
        }
    };

    const navigateTabs = (currentTab, direction) => {
        const tabs = getAllTabs();
        const currentIndex = tabs.indexOf(currentTab);

        if (currentIndex === -1) return;

        let newIndex;
        if (direction === 'next') {
            // Wrap to first tab when moving next from last tab
            newIndex = (currentIndex + 1) % tabs.length;
        } else if (direction === 'prev') {
            // Wrap to last tab when moving previous from first tab
            newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        }

        if (tabs[newIndex]) {
            tabs[newIndex].focus();
        }
    };

    const getAllTabs = () => {
        if (!wrapperRef.current) return [];

        // Find all elements with role="tab" within the wrapper
        const tablist = wrapperRef.current.querySelector('[role="tablist"]');
        if (!tablist) return [];

        return Array.from(tablist.querySelectorAll('[role="tab"]'));
    };

    const getFirstTab = () => {
        const tabs = getAllTabs();
        return tabs[0] || null;
    };

    const getLastTab = () => {
        const tabs = getAllTabs();
        return tabs[tabs.length - 1] || null;
    };

    return (
        <div
            ref={wrapperRef}
            onKeyDown={handleKeyDown}
            className={className}
            {...other}
        >
            {children}
        </div>
    );
});

export default TabAccessibilityWrap;
